/**
 * API 请求配置
 * 使用 /api 相对路径，通过 Vite/Nginx 代理到 api.fenleisaas.local
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * 获取当前站点ID - 从 localStorage 获取
 */
export function getCurrentSiteId(): string {
  return localStorage.getItem('current_site_id') || '';
}

/**
 * 设置当前站点ID
 */
export function setCurrentSiteId(siteId: string): void {
  if (siteId) {
    localStorage.setItem('current_site_id', siteId);
  }
}

/**
 * 请求封装
 */
interface RequestOptions extends RequestInit {
  params?: Record<string, string | number>;
}

export async function request<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { params, ...fetchOptions } = options;
  
  // 构建 URL
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      // 过滤掉 undefined、null、空字符串
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }
  
  // 获取 Token
  const token = localStorage.getItem('token');
  
  // 判断 body 是否为 FormData（如果是，不设置 Content-Type 让浏览器自动处理）
  const isFormData = fetchOptions.body instanceof FormData;
  
  // 设置默认 headers
  const headers: HeadersInit = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    'X-Site-Id': getCurrentSiteId(), // 动态获取站点ID
    ...fetchOptions.headers,
  };

  // 调试：打印请求信息
  if (options.method === 'PUT' || options.method === 'POST') {
    console.log(`📤 ${options.method} ${endpoint}:`, fetchOptions.body);
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    body: fetchOptions.body ? (isFormData ? fetchOptions.body : (typeof fetchOptions.body === 'string' ? fetchOptions.body : JSON.stringify(fetchOptions.body))) : undefined,
  });

  const data = await response.json();
  
  // 调试：打印响应信息
  console.log(`📥 ${options.method || 'GET'} ${endpoint} 响应:`, data);

  // 检查业务错误
  if (data.code !== 0) {
    throw new Error(data.message || '请求失败');
  }

  return data.data || data;
}

export default request;

/**
 * 更新个人资料
 */
export interface UpdateProfileData {
  nickname?: string;
  phone?: string;
  wechat?: string;
  avatar_url?: string;
  [key: string]: string | undefined;
}

export async function updateProfile(data: UpdateProfileData): Promise<void> {
  return request('/auth/profile', {
    method: 'PUT',
    body: data,
  });
}

/**
 * 上传图片
 */
export async function uploadImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('token');
  const siteId = getCurrentSiteId();

  const response = await fetch(`${API_BASE_URL}/upload`, {
    method: 'POST',
    headers: {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      'X-Site-Id': siteId,
    },
    body: formData,
  });

  const data = await response.json();
  if (data.code !== 0) {
    throw new Error(data.message || '上传失败');
  }

  return data.url;
}

// ==================== 用户端公开接口 ====================

// 使用函数获取站点ID（每次调用时从 localStorage 动态读取）
const getSiteId = (): string => getCurrentSiteId();

/** 用户端分类数据结构（扁平化，含子分类名列表） */
export interface FrontendCategory {
  id: string;
  name: string;
  slug: string;
  icon: string;
  sort_order: number;
  status: string;
  parent_id: string | null;
  children?: FrontendCategory[];
}

/**
 * 获取站点分类树（用户端公开）
 */
export async function getSiteCategories(): Promise<FrontendCategory[]> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/categories`, { params: { site_id: siteId } });
}

/**
 * 获取导航菜单（用户端公开）
 */
export async function getSiteNavMenus() {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/nav-menus`, { params: { site_id: siteId } });
}

/** getNavMenus 别名，供模板组件使用 */
export const getNavMenus = getSiteNavMenus;

/**
 * 获取页脚菜单（从站点设置中提取）
 */
export async function getFooterMenus(): Promise<FooterMenuItem[]> {
  const info = await getSiteInfo();
  return info?.footer_menus || [];
}

/**
 * 获取导航分类（is_nav=1 的分类）
 */
export async function getNavCategories(): Promise<Array<{ id: string; name: string; slug: string; icon: string }>> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/nav-categories`, { params: { site_id: siteId } });
}

/** 信息列表项数据结构（用户端） */
export interface ListingItem {
  id: string;
  title: string;
  content: string;
  images: string[];
  contact_name: string;
  contact_phone: string;
  contact_wechat: string;
  company_name: string;
  view_count: number;
  is_pinned: boolean;
  category: {
    id: string;
    name: string;
    slug: string;
  } | null;
  user: {
    id: string;
    nickname: string;
    avatar: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

/** 信息列表响应 */
export interface ListingsResponse {
  total: number;
  page: number;
  page_size: number;
  list: ListingItem[];
}

/**
 * 获取信息列表（用户端公开）
 * @param categoryId 可选，按分类筛选
 * @param keyword 可选，关键词搜索
 */
export async function getListings(params?: {
  category_id?: string;
  q?: string;
  page?: number;
  page_size?: number;
}): Promise<ListingsResponse> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/listings`, { params });
}

// ==================== 用户中心接口（需认证） ====================

/** 我的发布列表项数据结构 */
export interface MyListingItem {
  id: string;
  site_id: string;
  category_id: string;
  category_name: string;
  title: string;
  content: string;
  images: string[];
  contact_phone: string;
  contact_wechat: string;
  company_name: string;
  contact_name: string;
  view_count: number;
  status: 'pending' | 'active' | 'offline' | 'rejected';
  audit_status: 'pending' | 'active' | 'offline' | 'rejected';
  is_pinned: boolean;
  pinned_until: string | null;
  created_at: string;
  updated_at: string;
}

/** 发布限制信息 */
export interface PublishLimitInfo {
  is_vip: boolean;
  vip_expires_at: string | null;
  free_limit: number;
  published_count: number;
  remaining_free: number;
  charge_per_extra: number;
  can_publish: boolean;
}

/**
 * 获取发布限制信息
 */
export async function getPublishLimit(): Promise<PublishLimitInfo> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/publish-limit`);
}

/** 我的发布列表项数据结构 */
export interface MyListingItem {
  id: string;
  site_id: string;
  category_id: string;
  category_name: string;
  title: string;
  content: string;
  images: string[];
  contact_phone: string;
  view_count: number;
  status: 'pending' | 'active' | 'offline' | 'rejected';
  audit_status: 'pending' | 'active' | 'offline' | 'rejected';
  is_pinned: boolean;
  pinned_until: string | null;
  created_at: string;
  updated_at: string;
}

/** 我的发布列表响应 */
export interface MyListingsResponse {
  total: number;
  page: number;
  page_size: number;
  list: MyListingItem[];
}

/**
 * 获取我的发布列表
 */
export async function getMyListings(params?: { page?: number; page_size?: number; status?: string }): Promise<MyListingsResponse> {
  return request('/users/me/listings', { params });
}

/**
 * 创建发布信息
 */
export async function createMyListing(data: {
  category_id: string;
  title: string;
  content: string;
  images?: string[];
  company_name?: string;
  contact_name?: string;
  contact_phone: string;
  contact_wechat?: string;
}): Promise<{ id: string }> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/listings`, {
    method: 'POST',
    body: data,
  });
}

/**
 * 删除我的发布
 */
export async function deleteMyListing(id: string): Promise<void> {
  return request(`/users/me/listings/${id}`, { method: 'DELETE' });
}

/**
 * 上下架我的发布
 */
export async function toggleMyListingStatus(id: string): Promise<{ status: string }> {
  return request(`/users/me/listings/${id}/toggle-status`, { method: 'PUT' });
}

/**
 * 获取信息详情（用户端）
 */
export async function getListingDetail(listingId: string): Promise<{
  id: string;
  title: string;
  content: string;
  images: string[];
  category_id: string;
  category_name: string;
  company_name: string;
  contact_name: string;
  contact_phone: string;
  contact_wechat: string;
  view_count: number;
  status: string;
  created_at: string;
}> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/listings/${listingId}`);
}

/**
 * 更新我的发布
 */
export async function updateMyListing(listingId: string, data: {
  category_id?: string;
  title?: string;
  content?: string;
  images?: string[];
  company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_wechat?: string;
}): Promise<void> {
  const siteId = getSiteId();
  return request(`/sites/${siteId}/listings/${listingId}`, {
    method: 'PUT',
    body: data,
  });
}

// ==================== 后台管理接口（需管理员权限） ====================

/** 后台信息列表项 */
export interface AdminListingItem {
  id: string;
  title: string;
  category_id: string;
  category_name: string;
  company_name?: string;
  user_id: string;
  user_nickname: string;
  user_phone: string;
  content: string;
  images: string[];
  contact_name: string;
  contact_phone: string;
  contact_wechat: string;
  view_count: number;
  status: 'pending' | 'published' | 'offline';
  audit_status: 'pending' | 'approved' | 'rejected';
  reject_reason: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}

/** 后台信息列表响应 */
export interface AdminListingsResponse {
  total: number;
  page: number;
  page_size: number;
  list: AdminListingItem[];
}

/**
 * 获取后台信息列表（管理员）
 */
export async function getAdminListings(params?: {
  page?: number;
  page_size?: number;
  keyword?: string;
  status?: string;
  category_id?: string;
  date_start?: string;
  date_end?: string;
}): Promise<AdminListingsResponse> {
  return request('/admin/listings', { params });
}

/**
 * 后台信息批量导出（导出当前筛选条件下的全部信息，字段与批量导入模板一致）
 */
export async function exportAdminListings(params?: {
  keyword?: string;
  status?: string;
  category_id?: string;
  date_start?: string;
  date_end?: string;
}): Promise<{ total: number; list: AdminListingItem[] }> {
  return request('/admin/listings/export', { params });
}

/**
 * 获取后台信息详情（管理员）
 */
export async function getAdminListingDetail(id: string): Promise<{
  id: string;
  title: string;
  content: string;
  category_id: string;
  category_name: string;
  user_id: string;
  user_nickname: string;
  contact_phone: string;
  contact_wechat: string;
  company_name: string;
  contact_name: string;
  images: string[];
  view_count: number;
  status: string;
  reject_reason: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
}> {
  const res = await request<{ code: number; data: any }>(`/admin/listing-detail/${id}`);
  // 确保返回的数据是对象而非数组
  if (!res.data || typeof res.data !== 'object' || Array.isArray(res.data)) {
    throw new Error('数据格式错误');
  }
  return res.data;
}

/**
 * 审核通过信息（管理员）
 */
export async function approveListing(id: string): Promise<void> {
  return request(`/admin/listing-detail/${id}/approve`, { method: 'PUT' });
}

/**
 * 驳回信息（管理员）
 */
export async function rejectListing(id: string, reason: string): Promise<void> {
  return request(`/admin/listing-detail/${id}/reject`, { method: 'PUT', body: { reason } });
}

/**
 * 后台删除信息（管理员）
 */
export async function adminDeleteListing(id: string): Promise<void> {
  return request(`/admin/listing-detail/${id}`, { method: 'DELETE' });
}

/**
 * 后台上下架信息（管理员）
 */
export async function adminToggleListingStatus(id: string): Promise<{ status: string }> {
  return request(`/admin/listing-detail/${id}/toggle-status`, { method: 'PUT' });
}

/**
 * 后台编辑信息（管理员）
 */
export async function updateAdminListing(id: string, data: {
  category_id?: string;
  title?: string;
  content?: string;
  images?: string[];
  company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_wechat?: string;
  status?: string;
  created_at?: string;
}): Promise<void> {
  return request(`/admin/listing-detail/${id}`, {
    method: 'PUT',
    body: data,
  });
}

/**
 * 批量删除信息（管理员）
 */
export async function batchDeleteListings(ids: string[]): Promise<{ count: number }> {
  return request('/admin/listings/batch-delete', {
    method: 'POST',
    body: { ids },
  });
}

/**
 * 批量下架信息（管理员）
 */
export async function batchOfflineListings(ids: string[]): Promise<{ count: number }> {
  return request('/admin/listings/batch-offline', {
    method: 'POST',
    body: { ids },
  });
}

/**
 * 批量替换发布时间（管理员）
 */
export async function batchUpdateListingTime(ids: string[], times: string[]): Promise<{ count: number }> {
  return request('/admin/listings/batch-update-time', {
    method: 'POST',
    body: { ids, times },
  });
}

/**
 * 批量导入信息（管理员）
 */
export async function batchImportListings(data: Array<{
  title: string;
  category_id?: string;
  company_name?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_wechat?: string;
  content?: string;
  images?: string[];
}>): Promise<{ count: number; fail_count: number }> {
  return request('/admin/listings/batch-import', {
    method: 'POST',
    body: { listings: data },
  });
}

/**
 * 单页信息
 */
export interface SinglePage {
  id: string;
  title: string;
  slug: string;
  content: string;
  status: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * 获取单页详情
 */
export async function getSinglePage(slug: string): Promise<SinglePage | null> {
  try {
    const res = await request<SinglePage>(`/single-pages/${slug}`);
    return res;
  } catch (e) {
    return null;
  }
}

/**
 * 网站设置信息
 */
export interface FooterMenuItem {
  id: string;
  label: string;
  href: string;
  external?: boolean;
  enabled?: boolean;
}

export interface BannerItem {
  id: string;
  image: string;
  link: string;
  title: string;
  subtitle: string;
  enabled: boolean;
}

export interface SiteInfo {
  id: string;
  site_name: string;  // 网站设置-基本设置中的网站名称，空则不显示
  name: string;       // 展示名称（优先 site_settings.site_name，回退到 sites.name）
  logo: string;
  seo_title: string;
  seo_description: string;
  seo_keywords: string;
  icp: string;
  icp_url: string;
  footer_info: string;
  contact_info: {
    company_name?: string;
    phone?: string;
    email?: string;
    wechat?: string;
    qq?: string;
    address?: string;
    work_hours?: string;
  };
  footer_menus: FooterMenuItem[];
  search_enabled: boolean;
  search_disabled_message: string;
  banner_settings: BannerItem[];
  enable_vip: boolean;
  hide_company_name: boolean;
  // 模版/主题配置
  template_id?: string;
  theme_color?: string;
  login_style?: string;
  custom_colors?: string[];
}

/**
 * 获取网站设置信息
 */
export async function getSiteInfo(): Promise<SiteInfo | null> {
  try {
    const siteId = getSiteId();
    const res = await request<SiteInfo>(`/sites/${siteId}`);
    return res;
  } catch (e) {
    console.error('获取网站信息失败:', e);
    return null;
  }
}

// ==================== 用户可管理的站点 ====================

export interface MySiteInfo {
  id: string;
  name: string;
  subdomain: string;
  role: 'owner' | 'admin' | 'tenant';
  site_type?: string;
  status?: string;
  created_at?: string;
  expire_at?: string;
  owner_id?: string;
  tenant_id?: string;
  domains?: string[];
  owner_name?: string;
  province?: string;
  template_id?: string;
}

export async function getMySites(): Promise<{ list: MySiteInfo[]; total: number }> {
  return request('/auth/my-sites');
}

// ==================== 收藏 ====================

export interface FavoriteItem {
  id: string;
  listing_id: string;
  title: string;
  images: string[];
  category: string;
  summary: string;
  view_count: number;
  created_at: string;
  publisher: {
    id: string;
    nickname: string;
    avatar: string;
  } | null;
}

// 内部响应格式（带分页）
interface FavoritesResponse {
  total: number;
  page: number;
  page_size: number;
  list: Array<{
    id: string;
    listing: {
      id: string;
      title: string;
      images: string[];
      category_id: string;
      category_name: string;
      summary: string;
      view_count: number;
      user: { id: string; nickname: string; avatar: string } | null;
    };
    created_at: string;
  }>;
}

/**
 * 获取我的收藏列表
 */
export async function getMyFavorites(): Promise<FavoriteItem[]> {
  const res = await request<FavoritesResponse>('/favorites');
  return (res.list || []).map((item) => ({
    id: item.id,
    listing_id: item.listing?.id || '',
    title: item.listing?.title || '',
    images: item.listing?.images || [],
    category: item.listing?.category_name || item.listing?.category_id || '',
    summary: item.listing?.summary || '',
    view_count: item.listing?.view_count || 0,
    created_at: item.created_at,
    publisher: item.listing?.user || null,
  }));
}

/**
 * 获取收藏数量
 */
export async function getFavoritesCount(): Promise<number> {
  const res = await request<{ total: number }>('/favorites');
  return res.total || 0;
}

/**
 * 取消收藏
 */
export async function removeFavorite(id: string): Promise<void> {
  return request(`/favorites/${id}`, { method: 'DELETE' });
}

// ==================== 浏览记录 ====================

export interface HistoryItem {
  id: string;
  listing_id: string;
  title: string;
  images: string[];
  category: string;
  summary: string;
  view_count: number;
  viewed_at: string;
  publisher: {
    id: string;
    nickname: string;
    avatar: string;
  } | null;
}

// 内部响应格式（带分页）
interface HistoryResponse {
  total: number;
  page: number;
  page_size: number;
  list: Array<{
    id: string;
    listing: {
      id: string;
      title: string;
      images: string[];
      category_id: string;
      category_name: string;
      summary: string;
      view_count: number;
      user: { id: string; nickname: string; avatar: string } | null;
    };
    viewed_at: string;
  }>;
}

/**
 * 获取我的浏览记录
 */
export async function getMyHistory(): Promise<HistoryItem[]> {
  const res = await request<HistoryResponse>('/view-history');
  return (res.list || []).map((item) => ({
    id: item.id,
    listing_id: item.listing?.id || '',
    title: item.listing?.title || '',
    images: item.listing?.images || [],
    category: item.listing?.category_name || item.listing?.category_id || '',
    summary: item.listing?.summary || '',
    view_count: item.listing?.view_count || 0,
    viewed_at: item.viewed_at,
    publisher: item.listing?.user || null,
  }));
}

/**
 * 获取浏览记录数量
 */
export async function getHistoryCount(): Promise<number> {
  const res = await request<{ total: number }>('/view-history');
  return res.total || 0;
}

/**
 * 清空浏览记录
 */
export async function clearHistory(): Promise<void> {
  return request('/view-history', { method: 'DELETE' });
}

// ==================== VIP订单 ====================

export interface OrderItem {
  id: string;
  plan_name: string;
  amount: number;
  duration_months: number;
  status: 'pending' | 'paid' | 'cancelled';
  paid_at: string | null;
  expire_at: string | null;
  created_at: string;
}

// VIP订单响应格式（带分页）
interface VipOrdersResponse {
  total: number;
  page: number;
  page_size: number;
  list: OrderItem[];
}

/**
 * 获取我的订单列表
 */
export async function getMyOrders(): Promise<OrderItem[]> {
  const res = await request<VipOrdersResponse>('/vip/orders');
  return res.list || [];
}

/**
 * 获取订单数量
 */
export async function getOrdersCount(): Promise<number> {
  const res = await request<VipOrdersResponse>('/vip/orders');
  return res.total || 0;
}

// ==================== 用户统计 ====================

export interface UserStats {
  listings_count: number;
  favorites_count: number;
  history_count: number;
  orders_count: number;
}

/**
 * 获取用户统计信息
 */
export async function getUserStats(): Promise<UserStats> {
  const [listings, favorites, history, orders] = await Promise.all([
    getMyListings().catch(() => ({ total: 0 })),
    getFavoritesCount().catch(() => 0),
    getHistoryCount().catch(() => 0),
    getOrdersCount().catch(() => 0),
  ]);
  
  return {
    listings_count: typeof listings === 'object' && 'total' in listings ? listings.total : 0,
    favorites_count: favorites,
    history_count: history,
    orders_count: orders,
  };
}

